// A persona's sandbox browser: the Browserbase live view while a session is
// active (read-only — the iframe ignores pointer input, because the live view
// URL can control the browser), otherwise the last screenshot.
import { useEffect, useRef, useState } from 'react';
import { VIEWPORTS } from '../../convex/shared/validators';
import type { LiveSessionView } from '../../convex/shared/views';
import { Icon } from '../ui/Icon';

export function liveViewSrc(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set('navbar', 'false');
    return u.toString();
  } catch {
    return url;
  }
}

export function LiveScreen({ session, screenshotUrl, breakpoint = 'desktop', interactiveHint }: { session?: LiveSessionView; screenshotUrl?: string; breakpoint?: 'desktop' | 'mobile'; interactiveHint?: boolean }) {
  const bp = session?.breakpoint ?? breakpoint;
  const vp = VIEWPORTS[bp];
  const box = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0.25);
  const active = session && (session.status === 'active' || session.status === 'starting') && session.liveViewUrl;
  const shot = session?.lastScreenshotUrl ?? screenshotUrl;

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setScale(Math.min(el.clientWidth / vp.width, el.clientHeight / vp.height) || 0.25);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [vp.width, vp.height]);

  return (
    <div className={`live-screen is-${bp}`} ref={box} style={{ aspectRatio: `${vp.width} / ${vp.height}` }}>
      {active ? (
        <iframe
          title="Live browser view"
          src={liveViewSrc(session.liveViewUrl!)}
          style={{ width: vp.width, height: vp.height, transform: `scale(${scale})` }}
          sandbox="allow-scripts allow-same-origin"
          referrerPolicy="no-referrer"
          tabIndex={-1}
        />
      ) : shot ? (
        <img src={shot} alt="Latest screenshot of the persona's browser" loading="lazy" />
      ) : (
        <div className="live-empty">
          <Icon name={bp === 'mobile' ? 'mobile' : 'desktop'} size={28} />
          <span>Screen is off</span>
        </div>
      )}
      {active ? <span className="live-badge corner">Live</span> : null}
      {session?.currentTaskGoal && interactiveHint ? <span className="live-goal">{session.currentTaskGoal}</span> : null}
    </div>
  );
}
